import type { RouteLocationRaw } from 'vue-router';

import { withNext } from '@/domains/auth/login-next-storage';
import { accountList } from '@/platform/auth-runtime/token-storage';
import { route, type InfoType } from '@/platform/routing/targets';

// Every step-to-step target runs through withNext so the `?next` param
// survives the chain (account → user → sso → success). clustersTarget is
// deliberately the only exception — it's the post-login destination, and
// useLoginSubmit handles consumeLoginNext before falling through to it.

export function signUpTarget(): RouteLocationRaw {
  return withNext(route['/login']());
}

export function signInTarget(): RouteLocationRaw {
  return withNext(
    route['/login']({
      step: accountList.value.length > 0 ? 'choose' : 'account',
    }),
  );
}

export function accountInputTarget(aid?: string): RouteLocationRaw {
  return withNext(
    aid ? route['/login']({ step: 'account', aid }) : route['/login']({ step: 'account' }),
  );
}

export function chooseAccountTarget(): RouteLocationRaw {
  return withNext(route['/login']({ step: 'choose' }));
}

export function userCredentialTarget(
  aid: string,
  protocol?: { saml?: boolean; oidc?: boolean },
): RouteLocationRaw {
  const query: { step: 'user'; aid: string; saml?: '1'; oidc?: '1' } = { step: 'user', aid };
  if (protocol?.saml) query.saml = '1';
  if (protocol?.oidc) query.oidc = '1';
  return withNext(route['/login'](query));
}

export function ssoTarget(
  aid: string,
  protocol?: { saml?: boolean; oidc?: boolean },
): RouteLocationRaw {
  const query: { step: 'sso'; aid: string; saml?: '1'; oidc?: '1' } = { step: 'sso', aid };
  if (protocol?.saml) query.saml = '1';
  if (protocol?.oidc) query.oidc = '1';
  return withNext(route['/login'](query));
}

export function setupPasswordTarget(args: {
  token: string;
  email: string;
  aid: string;
}): RouteLocationRaw {
  return withNext(route['/login']({ step: 'setupPassword', ...args }));
}

export function infoTarget(
  infoType: InfoType,
  extras?: { email?: string; aid?: string },
): RouteLocationRaw {
  return withNext(route['/login']({ step: 'info', infoType, ...extras }));
}

// Post-password-set landing — AccountInput with the "new password set" tag.
// Pass the aid so the input field prefills instead of stranding a user who
// followed an email password link on a blank account-ID box.
// Post-password landing. Legacy showed the "new password set" tip only for
// the forgot-password / invite reset flows (handleClickGoLogin(true)); the
// initial invite-activation setup flow returned without it
// (handleClickGoLogin()). `showTip` preserves that split.
export function postPasswordSetTarget(aid?: string, showTip = true): RouteLocationRaw {
  return withNext(
    route['/login']({
      step: 'account' as const,
      ...(showTip ? { passwordTip: '1' as const } : {}),
      ...(aid ? { aid } : {}),
    }),
  );
}

// MFA login-time steps — no challenge data in the query (it lives in the
// in-memory mfa-challenge store).
export function mfaVerifyTarget(): RouteLocationRaw {
  return withNext(route['/login']({ step: 'mfaVerify' }));
}

export function mfaSetupTarget(): RouteLocationRaw {
  return withNext(route['/login']({ step: 'mfaSetup' }));
}

export function clustersTarget(): RouteLocationRaw {
  return route['/clusters']();
}
